import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { motion, AnimatePresence } from 'framer-motion';
import { useEffect, useState, useRef, useLayoutEffect } from 'react';
import { ChevronDown, Home, Wrench, Cpu, MessageSquare, BookOpen } from 'lucide-react';

const navLinkClass = ({ isActive }: { isActive: boolean }) =>
	`px-3 py-2 text-sm font-medium transition ${isActive ? 'text-primary dark:text-primary' : 'text-gray-600 hover:text-primary dark:text-gray-300 dark:hover:text-primary'} `;

export default function SiteLayout() {
    const location = useLocation();
    const [mobileOpen, setMobileOpen] = useState(false);
    const flyoutRef = useRef<HTMLDivElement | null>(null);
    const headerRef = useRef<HTMLDivElement | null>(null);
    const [menuTop, setMenuTop] = useState<number>(74);
    const [showMenuTrigger, setShowMenuTrigger] = useState<boolean>(true);
    const lastScrollY = useRef<number>(0);

    // Scroll to top when route changes
	useEffect(() => {
		window.scrollTo(0, 0);
		setMobileOpen(false);
        // recalc menu button position on navigation
        const el = headerRef.current;
        if (el) {
            const rect = el.getBoundingClientRect();
            setMenuTop(rect.bottom + 10);
        }
	}, [location.pathname]);

	// Close on outside click
	useEffect(() => {
		function onDocClick(e: MouseEvent) {
			if (!mobileOpen) return;
			if (flyoutRef.current && !flyoutRef.current.contains(e.target as Node)) {
				setMobileOpen(false);
			}
		}
		document.addEventListener('mousedown', onDocClick);
		return () => document.removeEventListener('mousedown', onDocClick);
	}, [mobileOpen]);

    // Update menu button position on resize
    useEffect(() => {
        function onResize() {
            const el = headerRef.current;
            if (el) {
                const rect = el.getBoundingClientRect();
                setMenuTop(rect.bottom + 10);
            }
        }
        onResize();
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    // Show/hide menu trigger based on scroll direction
    useEffect(() => {
        function onScroll() {
            const current = window.scrollY || 0;
            const delta = current - (lastScrollY.current || 0);
            // Threshold to avoid jitter
            const threshold = 4;
            if (Math.abs(delta) < threshold) return;
            if (delta > 0) {
                // Scrolling down -> hide
                setShowMenuTrigger(false);
            } else {
                // Scrolling up -> show
                setShowMenuTrigger(true);
            }
            lastScrollY.current = current;
        }
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll as any);
    }, []);

    // Ensure correct initial placement after first paint
    useLayoutEffect(() => {
        function recalc() {
            const el = headerRef.current;
            if (el) {
                const rect = el.getBoundingClientRect();
                setMenuTop(rect.bottom + 10);
            }
        }
        // run immediately after layout
        recalc();
        // run on next frame to account for fonts/async layout
        const raf = requestAnimationFrame(recalc);
        // also run after full window load
        window.addEventListener('load', recalc, { once: true });
        return () => cancelAnimationFrame(raf);
    }, []);

    return (
		<div className="min-h-full flex flex-col">
            <motion.header 
                ref={headerRef as any}
				initial={{ opacity: 0, y: -20 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.6 }}
                className="fixed top-0 left-0 right-0 z-40 border-b bg-white/70 backdrop-blur supports-[backdrop-filter]:bg-white/60 dark:bg-[#0b1220]/70 supports-[backdrop-filter]:dark:bg-[#0b1220]/60 dark:border-white/10"
			>
			<div className="container flex h-16 items-center justify-between relative">
					<motion.div
						initial={{ opacity: 0, x: -20 }}
						animate={{ opacity: 1, x: 0 }}
						transition={{ duration: 0.6, delay: 0.1 }}
					>
						<Link to="/" className="flex items-center gap-2">
							<img src="https://ubiquityglass.com/img/ubiquityLogoGradient.svg" alt="Ubiquity AutoGlass" className="h-8 md:h-[2.55rem] w-auto" />
							<span className="sr-only">Ubiquity AutoGlass</span>
						</Link>
					</motion.div>
					<motion.nav 
						initial={{ opacity: 0, y: -10 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.6, delay: 0.2 }}
						className="hidden md:flex items-center gap-1"
					>
						<NavLink to="/services" className={navLinkClass}>Services</NavLink>
						<NavLink to="/solutions" className={navLinkClass}>Solutions</NavLink>
						<NavLink to="/resources" className={navLinkClass}>Resources</NavLink>
						<NavLink to="/support" className={navLinkClass}>Support & Contact</NavLink>
					</motion.nav>

					{/* Mobile flyout trigger */}
                    <AnimatePresence initial={false}>
                        {showMenuTrigger && (
                            <motion.div
                                key="menu-trigger"
                                initial={{ opacity: 0, y: -8 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -12 }}
                                transition={{ duration: 0.2 }}
                                className="md:hidden fixed inset-x-0 mx-auto w-fit z-50"
                                style={{ top: menuTop }}
                            >
                                <motion.button
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.97 }}
                                    className="flex items-center gap-2 px-4 py-2 rounded-full animated-gradient-fast subtle-glow text-white shadow-md ring-1 ring-white/20 z-50"
                                    aria-expanded={mobileOpen}
                                    aria-controls="mobile-flyout"
                                    onClick={() => setMobileOpen((v) => !v)}
                                >
                                    <span className="text-sm font-semibold tracking-wide">Menu</span>
                                    <ChevronDown className={`w-4 h-4 transition-transform ${mobileOpen ? 'rotate-180' : ''}`} />
                                </motion.button>
                            </motion.div>
                        )}
                    </AnimatePresence>
					<motion.div 
						initial={{ opacity: 0, x: 20 }}
						animate={{ opacity: 1, x: 0 }}
						transition={{ duration: 0.6, delay: 0.3 }}
						className="flex items-center gap-2"
					>
						<motion.a 
							href="https://getstarted.ubiquityglass.com" 
							target="_blank" 
							rel="noreferrer"
							whileHover={{ scale: 1.05 }}
							whileTap={{ scale: 0.95 }}
						>
							<Button variant="outline" size="sm" className="border-royalpurple text-royalpurple">Get Started</Button>
						</motion.a>
					<motion.a 
						href="https://realm.ubiquityglass.com" 
							target="_blank" 
							rel="noreferrer"
							whileHover={{ scale: 1.05 }}
							whileTap={{ scale: 0.95 }}
						>
							<Button size="sm" className="animated-gradient-fast subtle-glow text-white">Login</Button>
						</motion.a>
					</motion.div>
				</div>

			{/* Mobile flyout panel */}
            <AnimatePresence>
				{mobileOpen && (
                    <motion.div
						id="mobile-flyout"
						ref={flyoutRef}
                        initial={{ opacity: 0, y: -8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -6 }}
                        transition={{ duration: 0.2 }}
                        className="md:hidden fixed left-0 right-0 top-[4.5rem] mt-2 z-40"
					>
                        <motion.div layout className="mx-auto w-[94%] max-w-md rounded-3xl overflow-hidden max-h-[70vh] flex flex-col">
                            {/* Gradient border wrapper */}
                            <div className="p-[1px] rounded-3xl animated-gradient-fast subtle-glow">
                                <div className="rounded-3xl bg-white/80 backdrop-blur-xl border border-white/40 shadow-2xl">
                                    <div className="flex items-center justify-between px-4 py-3 border-b border-white/30">
                                <div className="text-sm font-semibold text-gray-700">Navigate</div>
                                        <button onClick={() => setMobileOpen(false)} className="text-sm font-medium text-gray-600 hover:text-primary">Close</button>
                                    </div>
                                    <motion.div
                                        className="grid grid-cols-3 gap-5 px-4 py-6 place-items-center overflow-y-auto"
                                        initial="hidden"
                                        animate="show"
                                        variants={{ hidden: {}, show: { transition: { staggerChildren: 0.06 } } }}
                                    >
                                        {[
                                            { to: '/', label: 'Home', Icon: Home },
                                            { to: '/services', label: 'Services', Icon: Wrench },
                                            { to: '/solutions', label: 'Solutions', Icon: Cpu },
                                    { to: '/resources', label: 'Resources', Icon: BookOpen },
                                    { to: '/support', label: 'Support', Icon: MessageSquare },
                                        ].map(({ to, label, Icon }) => (
                                            <motion.div
                                                key={label}
                                                variants={{ hidden: { opacity: 0, y: 12, scale: 0.96 }, show: { opacity: 1, y: 0, scale: 1 } }}
                                                transition={{ type: 'spring', stiffness: 260, damping: 20 }}
                                                whileHover={{ y: -2 }}
                                                whileTap={{ scale: 0.98 }}
                                                className="w-24"
                                            >
                                                <NavLink to={to as string} onClick={() => setMobileOpen(false)} className={({isActive}) => `group block rounded-2xl p-[1px] ${isActive ? 'animated-gradient-fast subtle-glow' : 'border border-gray-200'}`}>
                                                    <div className="rounded-2xl bg-white text-center p-4 transition group-hover:bg-white/95">
                                                        <Icon className="w-6 h-6 mx-auto text-gray-800 group-hover:text-babyblue" />
                                                        <div className="mt-2 text-xs font-semibold text-gray-800 group-hover:text-babyblue">{label}</div>
                                                    </div>
                                                </NavLink>
                                            </motion.div>
                                        ))}
                                    </motion.div>
                                </div>
                            </div>
                        </motion.div>
					</motion.div>
				)}
			</AnimatePresence>

		</motion.header>
			<main className="flex-1 pt-16">
				<Outlet key={location.pathname} />
			</main>
			<footer className="border-t py-12 text-sm text-gray-600">
				<div className="container grid md:grid-cols-4 gap-8">
					<div>
						<img src="https://ubiquityglass.com/img/ubiquityLogoGradient.svg" alt="Ubiquity AutoGlass" className="h-7 md:h-8 w-auto" />
						<p className="mt-2">AI-powered glass repair and replacement services with advanced automation and safety standards.</p>
						<div className="mt-4">
							<div className="font-semibold">Headquarters:</div>
							<p className="text-sm">575 S 143rd Ave, Unit 08<br />Goodyear, AZ 85338</p>
						</div>
						<div className="mt-2">
							<div className="font-semibold">Contact:</div>
							<p className="text-sm">support@ubiquityglass.com</p>
						</div>
					</div>
					<div>
						<div className="font-semibold">Services</div>
						<ul className="mt-2 space-y-1">
							<li><Link to="/services/windshield-repair" className="hover:text-secondary">Windshield Repair</Link></li>
							<li><Link to="/services/windshield-replacement" className="hover:text-secondary">Windshield Replacement</Link></li>
							<li><Link to="/services/side-glass" className="hover:text-secondary">Side Glass Replacement</Link></li>
							<li><Link to="/services/roof-back-glass" className="hover:text-secondary">Roof & Back Glass</Link></li>
							<li><Link to="/services/adas-calibrations" className="hover:text-secondary">ADAS Calibrations</Link></li>
							<li><Link to="/services/diagnostics-coding" className="hover:text-secondary">Diagnostics & Coding</Link></li>
						</ul>
					</div>
					<div>
						<div className="font-semibold">Solutions</div>
						<ul className="mt-2 space-y-1">
							<li><Link to="/solutions" className="hover:text-secondary">FrontLine</Link></li>
							<li><Link to="/solutions" className="hover:text-secondary">The Realm</Link></li>
							<li><Link to="/solutions" className="hover:text-secondary">FastLane</Link></li>
							<li><Link to="/solutions" className="hover:text-secondary">API Docs</Link></li>
							<li><Link to="/solutions" className="hover:text-secondary">Integrations</Link></li>
						</ul>
					</div>
					<div>
						<div className="font-semibold">Support & Contact</div>
						<ul className="mt-2 space-y-1">
							<li><Link to="/support" className="hover:text-secondary">FAQ</Link></li>
							<li><Link to="/support" className="hover:text-secondary">Contact Us</Link></li>
							<li><Link to="/about" className="hover:text-secondary">About Us</Link></li>
							<li><Link to="/terms" className="hover:text-secondary">Terms of Use</Link></li>
							<li><Link to="/privacy" className="hover:text-secondary">Privacy Policy</Link></li>
						</ul>
					</div>
				</div>
				<div className="container mt-8 gradient-divider" />
				<div className="container mt-6 flex flex-col md:flex-row gap-4 md:items-center md:justify-between">
					<div>© 2026 Ubiquity AutoGlass, Inc. All Rights Reserved.</div>
					<div className="text-gray-500">Built with AI automation and care.</div>
				</div>
			</footer>
		</div>
	);
}
